/**
 * ProductForm Page / Modal
 *
 * Full-page form for creating and editing products.
 * Also supports modal mode for use in Product Hierarchy panels.
 *
 * Props (modal mode only):
 *   isModal          {boolean}  - render as modal overlay instead of full page
 *   lockedProductType {string}  - pre-set and lock the product type dropdown
 *   onSuccess        {function} - called with saved product on success (modal mode)
 *   onCancel         {function} - called when user cancels (modal mode)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import api from '../services/api';
import { ROUTES } from '../config/constants';
import ProgressIndicator from '../components/IntakeFlow/ProgressIndicator';
import './ProductForm.css';

const HELP_THRESHOLD = 120;
function FieldHelp({ text }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  if (text.length <= HELP_THRESHOLD) {
    return <p className="product-form__field-help">{text}</p>;
  }
  return (
    <div className="product-form__field-learn-more">
      <button
        type="button"
        className="product-form__field-learn-more-toggle"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="product-form__field-learn-more-icon">ℹ</span>
        {open ? 'Hide detail ▲' : 'Learn more ▼'}
      </button>
      {open && <p className="product-form__field-learn-more-text">{text}</p>}
    </div>
  );
}

const PRODUCT_TYPE_OPTIONS = [
  { label: 'Technical Product', value: 'TECHNICAL' },
  { label: 'Commercial Product', value: 'COMMERCIAL' },
  { label: 'Package', value: 'PACKAGE' },
];

const BUSINESS_UNIT_OPTIONS = [
  'Analytics', 'Application Platform', 'Cloud Infrastructure', 'Data',
  'Digital', 'Ecosystem', 'InsuranceNow', 'InsuranceSuite', 'Not Applicable',
];

// Three-level hierarchy: Business Unit → Performance Matrix Group → Product Family
const PRODUCT_HIERARCHY = {
  Analytics: {
    Cyence:           ['Cyence Accumulation', 'Cyence Health Check', 'Cyence Risk'],
    HazardHub:        ['Agent Risk View', 'HH Casualty', 'HH Permit Data', 'HH Property', 'HH Replacement Cost', 'HH Single Peril', 'HH Premium Peril'],
    'Industry Intel': ['Claims Intel'],
    Predict:          ['Predict for Claims', 'Predict for Profitability'],
    PricingCenter:    ['Pricing'],
    'AI Assistant':   ['ProNavigator'],
    'Other ADS':      ['Canvas', 'Compare', 'Spotlight', 'Weather Discrepancy Alerts'],
  },
  'Cloud Infrastructure': {
    'Platform GWCP': ['Advanced Search', 'Data Masking', 'Disaster Recovery', 'Editions', 'Guidewire Credits', 'Partner Environment', 'Read Replica', 'Snapshot Export'],
  },
  Data: {
    Data: ['Data Studio', 'DataHub', 'Explore', 'IBM Cognos', 'InfoCenter', 'Millbrook', 'SAP Data Integrator', 'Standard Reporting'],
  },
  Digital: {
    Digital: ['EnterpriseEngage', 'Guidewire for Salesforce', 'Jutro Digital Platform'],
  },
  Ecosystem: {
    Partner: ['Referral', 'Resell', 'PartnerConnect'],
  },
  InsuranceNow: {
    InsuranceNow: ['Cloud Support and Maintenance', 'Editions', 'Guidewire Credits', 'Insights', 'InsuranceNow', 'Looker'],
  },
  InsuranceSuite: {
    BillingCenter:       ['BillingCenter', 'Client Data Management', 'Cloud Data Access', 'Cloud Support and Maintenance', 'Market Package'],
    ClaimCenter:         ['Autopilot Workflow Service', 'ClaimCenter', 'Client Data Management', 'Cloud Data Access', 'Cloud Support and Maintenance', 'Coinsurance', 'Market Package', 'Multicurrency'],
    PolicyCenter:        ['Autopilot Workflow Service', 'Client Data Management', 'Cloud Data Access', 'Cloud Support and Maintenance', 'Market Package', 'PolicyCenter', 'Quoting', 'Rating', 'Reinsurance', 'Standards Based Templates', 'Submission Intake', 'Underwriting Management'],
    UnderwritingCenter:  ['UnderwritingCenter', 'Underwriting Assistant'],
    'Other Core':        ['Client Data Management', 'Cloud Support and Maintenance', 'InsuranceSuite', 'Market Package'],
  },
  'Not Applicable': {
    'Not Applicable': ['Not Applicable'],
  },
};

// Flat fallback lists for the legacy 'Application Platform' BU (no hierarchy mapping)
const ALL_PMGS = [...new Set(Object.values(PRODUCT_HIERARCHY).flatMap(m => Object.keys(m)))].sort();
const ALL_PRODUCT_FAMILIES = [...new Set(Object.values(PRODUCT_HIERARCHY).flatMap(m => Object.values(m).flat()))].sort();

const INSTALLATION_OPTIONS = ['Guidewire Cloud', 'Self-Managed', 'Non-Applicable'];
const CORE_PRODUCT_TYPE_OPTIONS = ['Core Cloud', 'Core Term.'];
const PROVISIONED_IN_OPTIONS = ['NPE', 'Prod', 'NPE + Prod'];
const FULFILMENT_QTY_OPTIONS = ['Always One', 'Aggregate'];
const PROVISIONING_TEAM_OPTIONS = [
  'Cloud Ops',
  'Customer Self-Provisioning',
  'GGS/SRE',
  'GGS',
  'Self-service (PPP), otherwise GGS/SRE',
  'Sales Ops',
  'Pietown pod',
  'SRE Platforms',
  'Core team to register the applications',
  'SRE Platform',
  'Vulcan',
  'Self-Serve from GH',
  'PgM',
  'GGS Platform Support',
  'Platform Support',
];

const RELEASE_TYPE_OPTIONS = [
  { label: 'EA', value: 'EA' },
  { label: 'RA', value: 'RA' },
  { label: 'GA', value: 'GA' },
];

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const ProductForm = ({ isModal = false, lockedProductType = null, onSuccess, onCancel }) => {
  // In modal mode useParams returns empty — id always comes from URL in full-page mode
  const { id } = useParams();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const defaultType = lockedProductType || 'COMMERCIAL';

  const [formData, setFormData] = useState({
    // Basic
    product_name: '',
    product_type: defaultType,
    release_type: 'GA',
    product_code: '',
    description: '',
    categorization: '',
    owner: '',
    status: 'active',
    // Detail — Commercial
    business_unit: '',
    performance_matrix_group: '',
    product_family: '',
    availability_date: '',
    installation: '',
    end_of_life_date: '',
    discontinued_date: '',
    core_product_type: '',
    has_customer_update_adoption_criteria: 'false',
    based_on: '',
    // Detail — Commercial + Technical
    predecessor_product: '',
    // Detail — Technical
    fulfilment_qty_calc_method: '',
    polaris_id: '',
    ppp_apply: '',
    // Provisioning — Technical
    provisioning_required: '',
    provisioning_team: '',
    provisioning_automated: '',
    provisioned_in_npe_prod: '',
    has_provisioning_action_on_renewal: '',
  });

  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [hasLinks, setHasLinks] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentSection, setCurrentSection] = useState(0);
  const [pppCheckMessage, setPppCheckMessage] = useState(null);

  // Sections depend on product type
  const steps = useMemo(() => {
    const s = [{ title: 'Basic Information' }];
    if (formData.product_type === 'COMMERCIAL' || formData.product_type === 'TECHNICAL') {
      s.push({ title: 'Detail' });
    }
    if (formData.product_type === 'TECHNICAL') {
      s.push({ title: 'Provisioning' });
    }
    return s;
  }, [formData.product_type]);

  // Clamp currentSection if product_type changes reduce step count
  useEffect(() => {
    if (currentSection >= steps.length) {
      setCurrentSection(steps.length - 1);
    }
  }, [steps.length, currentSection]);

  // Overall completion percentage across all required fields
  const completionPercentage = useMemo(() => {
    const required = [
      formData.product_name.trim(),
      formData.product_type,
      formData.release_type,
      formData.categorization,
      formData.owner,
      formData.status,
    ];
    if (formData.product_type === 'COMMERCIAL') {
      required.push(formData.business_unit, formData.product_family, formData.based_on.trim());
    }
    if (formData.product_type === 'TECHNICAL') {
      required.push(formData.provisioning_required);
      if (formData.provisioning_required === 'true') {
        required.push(
          formData.provisioning_team.trim(),
          formData.provisioning_automated,
          formData.provisioned_in_npe_prod,
          formData.has_provisioning_action_on_renewal
        );
      }
    }
    const filled = required.filter(Boolean).length;
    return Math.round((filled / required.length) * 100);
  }, [formData]);

  // Cascading options: PMGs filtered by BU, PFs filtered by PMG
  const availablePMGs = useMemo(() => {
    const mapping = PRODUCT_HIERARCHY[formData.business_unit];
    const list = mapping ? Object.keys(mapping) : ALL_PMGS;
    // Include current value if it's a legacy/unmapped entry so the select renders it
    if (formData.performance_matrix_group && !list.includes(formData.performance_matrix_group)) {
      return [...list, formData.performance_matrix_group];
    }
    return list;
  }, [formData.business_unit, formData.performance_matrix_group]);

  const availablePFs = useMemo(() => {
    const mapping = PRODUCT_HIERARCHY[formData.business_unit];
    if (!mapping) return ALL_PRODUCT_FAMILIES; // legacy BU — show all
    const families = mapping[formData.performance_matrix_group] || [];
    // Include current value if it's a legacy/unmapped entry so the select renders it
    if (formData.product_family && !families.includes(formData.product_family)) {
      return [...families, formData.product_family];
    }
    return families;
  }, [formData.business_unit, formData.performance_matrix_group, formData.product_family]);

  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.getAllUsers();
        setUsers(response.data.data.users || []);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchUsers();
  }, []);

  // Fetch existing product in edit mode (full-page only)
  useEffect(() => {
    if (isEditMode && !isModal) {
      const fetchProduct = async () => {
        try {
          setLoading(true);
          const response = await api.getProductById(id);
          const product = response.data.data.product || response.data.data;
          setFormData({
            product_name: product.product_name || '',
            product_type: product.product_type || 'COMMERCIAL',
            release_type: product.release_type || 'GA',
            product_code: product.product_code || '',
            description: product.description || '',
            categorization: product.categorization || '',
            owner: product.owner_id ? String(product.owner_id) : '',
            status: product.status || 'active',
            business_unit: product.business_unit || '',
            performance_matrix_group: product.performance_matrix_group || '',
            product_family: product.product_family || '',
            availability_date: product.availability_date || '',
            installation: product.installation || '',
            end_of_life_date: product.end_of_life_date || '',
            discontinued_date: product.discontinued_date || '',
            core_product_type: product.core_product_type || '',
            has_customer_update_adoption_criteria: product.has_customer_update_adoption_criteria != null ? String(product.has_customer_update_adoption_criteria) : 'false',
            based_on: product.based_on || '',
            predecessor_product: product.predecessor_product || '',
            fulfilment_qty_calc_method: product.fulfilment_qty_calc_method || '',
            polaris_id: product.polaris_id || '',
            ppp_apply: product.ppp_apply != null ? String(product.ppp_apply) : '',
            provisioning_required: product.provisioning_required != null ? String(product.provisioning_required) : '',
            provisioning_team: product.provisioning_team || '',
            provisioning_automated: product.provisioning_automated != null ? String(product.provisioning_automated) : '',
            provisioned_in_npe_prod: product.provisioned_in_npe_prod || '',
            has_provisioning_action_on_renewal: product.has_provisioning_action_on_renewal != null ? String(product.has_provisioning_action_on_renewal) : '',
          });
          if (product.linkedCount > 0) {
            setHasLinks(true);
          }
        } catch (err) {
          console.error('Error fetching product:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchProduct();
    }
  }, [id, isEditMode, isModal]);

  // When editing a product that already has ppp_apply=true, run the existence check on load
  useEffect(() => {
    if (formData.ppp_apply === 'true' && formData.product_name) {
      api.checkPPPExists({ product_name: formData.product_name })
        .then(response => {
          const exists = response.data.data.exists;
          setPppCheckMessage(exists
            ? 'A PPP request exists for this product.'
            : 'No PPP request exists for this product.'
          );
        })
        .catch(() => setPppCheckMessage(null));
    } else {
      setPppCheckMessage(null);
    }
  }, [formData.ppp_apply, formData.product_name]);

  // Reset has_customer_update_adoption_criteria to false when release type is not GA
  useEffect(() => {
    if (formData.release_type !== 'GA') {
      setFormData(prev => ({ ...prev, has_customer_update_adoption_criteria: 'false' }));
    }
  }, [formData.release_type]);

  const COMMERCIAL_FIELDS = [
    'business_unit', 'performance_matrix_group', 'product_family', 'based_on',
    'availability_date', 'installation', 'end_of_life_date', 'discontinued_date',
    'core_product_type',
  ];
  const TECHNICAL_FIELDS = [
    'fulfilment_qty_calc_method', 'polaris_id', 'has_customer_update_adoption_criteria', 'ppp_apply',
    'provisioning_required', 'provisioning_team', 'provisioning_automated',
    'provisioned_in_npe_prod', 'has_provisioning_action_on_renewal',
  ];

  const handleChange = (field, value) => {
    if (field === 'product_type') {
      const clearFields = value === 'TECHNICAL' ? COMMERCIAL_FIELDS
        : value === 'COMMERCIAL' ? TECHNICAL_FIELDS
        : [...COMMERCIAL_FIELDS, ...TECHNICAL_FIELDS];
      const cleared = Object.fromEntries(clearFields.map(f => [f, '']));
      setFormData(prev => ({ ...prev, product_type: value, ...cleared }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.product_name.trim()) newErrors.product_name = 'Product name is required';
    if (!formData.product_code.trim()) newErrors.product_code = 'Product code is required';
    if (!formData.product_type) newErrors.product_type = 'Product type is required';
    if (!formData.release_type) newErrors.release_type = 'Release type is required';
    if (!formData.categorization) newErrors.categorization = 'Categorization is required';
    if (!formData.owner) newErrors.owner = 'Owner is required';
    if (!formData.status) newErrors.status = 'Status is required';
    // Commercial Detail mandatory fields
    if (formData.product_type === 'COMMERCIAL') {
      if (!formData.business_unit) newErrors.business_unit = 'Business Unit is required';
      if (!formData.performance_matrix_group) newErrors.performance_matrix_group = 'Performance Matrix Group is required';
      if (!formData.product_family) newErrors.product_family = 'Product Family is required';
      if (!formData.based_on.trim()) newErrors.based_on = 'Based On is required';
    }
    // Technical Detail + Provisioning mandatory fields
    if (formData.product_type === 'TECHNICAL') {
      if (!formData.has_customer_update_adoption_criteria) newErrors.has_customer_update_adoption_criteria = 'Has Customer Update Adoption Criteria is required';
      if (!formData.provisioning_required) newErrors.provisioning_required = 'Provisioning Required is required';
      if (formData.provisioning_required === 'true') {
        if (!formData.provisioning_team.trim()) newErrors.provisioning_team = 'Provisioning Team is required';
        if (!formData.provisioning_automated) newErrors.provisioning_automated = 'Provisioning Automated is required';
        if (!formData.provisioned_in_npe_prod) newErrors.provisioned_in_npe_prod = 'Provisioned in NPE/Prod is required';
        if (!formData.has_provisioning_action_on_renewal) newErrors.has_provisioning_action_on_renewal = 'Has Provisioning Action on Renewal is required';
      }
    }
    return newErrors;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      const fieldLabels = {
        product_name: 'Product Name',
        product_type: 'Product Type',
        release_type: 'Release Type',
        categorization: 'Categorization',
        owner: 'Owner',
        status: 'Status',
        business_unit: 'Business Unit',
        performance_matrix_group: 'Performance Matrix Group',
        product_family: 'Product Family',
        based_on: 'Based On',
        has_customer_update_adoption_criteria: 'Has Customer Update Adoption Criteria',
        provisioning_required: 'Provisioning Required',
        provisioning_team: 'Provisioning Team',
        provisioning_automated: 'Provisioning Automated',
        provisioned_in_npe_prod: 'Provisioned in NPE/Prod',
        has_provisioning_action_on_renewal: 'Has Provisioning Action on Renewal',
      };
      const missing = Object.keys(validationErrors).map(k => fieldLabels[k] || k).join(', ');
      setErrors({ ...validationErrors, _general: `Required fields missing: ${missing}` });
      // Scroll to top of modal/form so user sees the error banner
      const container = document.querySelector('.product-form-modal__container') || document.querySelector('.product-form');
      if (container) container.scrollTop = 0;
      return;
    }

    try {
      setSaving(true);
      setErrors({});

      const isCommercial = formData.product_type === 'COMMERCIAL';
      const isTechnical = formData.product_type === 'TECHNICAL';
      const provReq = formData.provisioning_required === 'true';

      const payload = {
        product_name: formData.product_name.trim(),
        product_type: formData.product_type,
        release_type: formData.release_type,
        product_code: formData.product_code.trim(),
        description: formData.description.trim() || null,
        categorization: formData.categorization,
        owner_id: formData.owner || null,
        status: formData.status,
        // Detail — Commercial
        business_unit: isCommercial ? (formData.business_unit || null) : null,
        performance_matrix_group: isCommercial ? (formData.performance_matrix_group || null) : null,
        product_family: isCommercial ? (formData.product_family || null) : null,
        availability_date: isCommercial ? (formData.availability_date || null) : null,
        installation: isCommercial ? (formData.installation || null) : null,
        end_of_life_date: isCommercial ? (formData.end_of_life_date || null) : null,
        discontinued_date: isCommercial ? (formData.discontinued_date || null) : null,
        core_product_type: isCommercial ? (formData.core_product_type || null) : null,
        has_customer_update_adoption_criteria: (isTechnical && formData.release_type === 'GA') ? (formData.has_customer_update_adoption_criteria === 'true') : false,
        based_on: isCommercial ? (formData.based_on.trim() || null) : null,
        // Detail — Commercial + Technical
        predecessor_product: (isCommercial || isTechnical) ? (formData.predecessor_product.trim() || null) : null,
        // Detail — Technical
        fulfilment_qty_calc_method: isTechnical ? (formData.fulfilment_qty_calc_method.trim() || null) : null,
        polaris_id: isTechnical ? (formData.polaris_id.trim() || null) : null,
        ppp_apply: isTechnical ? (formData.ppp_apply === 'true' ? true : formData.ppp_apply === 'false' ? false : null) : null,
        // Provisioning — Technical
        provisioning_required: isTechnical ? (formData.provisioning_required === 'true' ? true : formData.provisioning_required === 'false' ? false : null) : null,
        provisioning_team: (isTechnical && provReq) ? (formData.provisioning_team.trim() || null) : null,
        provisioning_automated: (isTechnical && provReq) ? (formData.provisioning_automated === 'true' ? true : formData.provisioning_automated === 'false' ? false : null) : null,
        provisioned_in_npe_prod: (isTechnical && provReq) ? (formData.provisioned_in_npe_prod || null) : null,
        has_provisioning_action_on_renewal: (isTechnical && provReq) ? (formData.has_provisioning_action_on_renewal === 'true' ? true : formData.has_provisioning_action_on_renewal === 'false' ? false : null) : null,
      };

      let savedProduct;
      if (isEditMode) {
        const response = await api.updateProduct(id, payload);
        savedProduct = response.data.data.product || response.data.data;
      } else {
        const response = await api.createProduct(payload);
        savedProduct = response.data.data.product || response.data.data;
      }

      if (isModal && onSuccess) {
        onSuccess(savedProduct);
      } else {
        setSuccessMessage(isEditMode ? 'Product updated successfully.' : 'Product created successfully.');
        setTimeout(() => {
          history.push(ROUTES.DASHBOARD);
        }, 800);
      }
    } catch (err) {
      console.error('Error saving product:', err);
      if (err.status === 409 || (err.message && err.message.toLowerCase().includes('already exists'))) {
        const msg = err.message || 'A product with this name already exists.';
        if (msg.toLowerCase().includes('code')) {
          setErrors({ product_code: msg, _general: msg });
        } else {
          setErrors({ product_name: msg, _general: msg });
        }
      } else {
        setErrors({ _general: err.message || 'Failed to save product. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const sectionFieldLabels = {
    product_name: 'Product Name',
    product_type: 'Product Type',
    release_type: 'Release Type',
    categorization: 'Categorization',
    owner: 'Owner',
    status: 'Status',
    business_unit: 'Business Unit',
    product_family: 'Product Family',
    based_on: 'Based On',
    provisioning_required: 'Provisioning Required',
    provisioning_team: 'Provisioning Team',
    provisioning_automated: 'Provisioning Automated',
    provisioned_in_npe_prod: 'Provisioned in NPE/Prod',
    has_provisioning_action_on_renewal: 'Has Provisioning Action on Renewal',
  };

  const validateSection = useCallback((sectionIndex) => {
    const errs = {};
    if (sectionIndex === 0) {
      if (!formData.product_name.trim()) errs.product_name = 'Product name is required';
      if (!formData.product_type) errs.product_type = 'Product type is required';
      if (!formData.release_type) errs.release_type = 'Release type is required';
      if (!formData.categorization) errs.categorization = 'Categorization is required';
      if (!formData.owner) errs.owner = 'Owner is required';
      if (!formData.status) errs.status = 'Status is required';
    } else if (sectionIndex === 1 && formData.product_type === 'COMMERCIAL') {
      if (!formData.business_unit) errs.business_unit = 'Business Unit is required';
      if (!formData.product_family) errs.product_family = 'Product Family is required';
      if (!formData.based_on.trim()) errs.based_on = 'Based On is required';
    }
    return errs;
  }, [formData]);

  const scrollToTop = () => {
    const container = document.querySelector('.product-form-modal__container') || document.querySelector('.product-form');
    if (container) container.scrollTop = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNext = () => {
    const errs = validateSection(currentSection);
    if (Object.keys(errs).length > 0) {
      const missing = Object.keys(errs).map(k => sectionFieldLabels[k] || k).join(', ');
      setErrors({ ...errs, _general: `Required fields missing: ${missing}` });
      scrollToTop();
      return;
    }
    setErrors({});
    setCurrentSection(prev => Math.min(prev + 1, steps.length - 1));
    scrollToTop();
  };

  const handleSectionBack = () => {
    setErrors({});
    setCurrentSection(prev => Math.max(prev - 1, 0));
    scrollToTop();
  };

  const handleStepClick = useCallback((targetIndex) => {
    if (targetIndex === currentSection) return;
    if (targetIndex < currentSection) {
      setErrors({});
      setCurrentSection(targetIndex);
      scrollToTop();
      return;
    }
    // Forward navigation: validate each intermediate section
    for (let i = currentSection; i < targetIndex; i++) {
      const errs = validateSection(i);
      if (Object.keys(errs).length > 0) {
        const missing = Object.keys(errs).map(k => sectionFieldLabels[k] || k).join(', ');
        setErrors({ ...errs, _general: `Required fields missing: ${missing}` });
        setCurrentSection(i);
        scrollToTop();
        return;
      }
    }
    setErrors({});
    setCurrentSection(targetIndex);
    scrollToTop();
  }, [currentSection, validateSection, steps.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = () => {
    if (isModal && onCancel) {
      onCancel();
    } else {
      history.push(ROUTES.DASHBOARD);
    }
  };

  const typeIsLocked = isModal && Boolean(lockedProductType);
  const typeIsDisabledByLinks = isEditMode && hasLinks;

  const pageTitle = isEditMode
    ? `Edit Product${formData.product_name ? ' — ' + formData.product_name : ''}`
    : lockedProductType === 'PACKAGE'
      ? 'Create Package'
      : lockedProductType === 'TECHNICAL'
        ? 'Create Technical Product'
        : 'Create Product';

  const formContent = (
    <>
      {successMessage && (
        <div className="product-form__success">{successMessage}</div>
      )}

      <ProgressIndicator
        steps={steps}
        currentStep={currentSection}
        completionPercentage={completionPercentage}
        onStepClick={handleStepClick}
        visibleStepIndices={new Set(steps.map((_, i) => i))}
      />

      {errors._general && (
        <div className="product-form__error-banner">{errors._general}</div>
      )}

      <div className="product-form__body">
        {/* ── Section 0: Basic Information ──────────────────────────────────── */}
        {currentSection === 0 && (
        <div className="product-form__section">
          <h2 className="product-form__section-title">Basic Information</h2>
          <div className="product-form__grid">

            {/* Product Name */}
            <div className="product-form__field product-form__field--full">
              <label className="product-form__label" htmlFor="product_name">
                Product Name <span className="product-form__required">*</span>
              </label>
              <input
                id="product_name"
                type="text"
                className={`product-form__input ${errors.product_name ? 'product-form__input--error' : ''}`}
                value={formData.product_name}
                onChange={e => handleChange('product_name', e.target.value)}
                placeholder="Enter product name"
              />
              {errors.product_name && (
                <span className="product-form__field-error">{errors.product_name}</span>
              )}
              <FieldHelp text="Is the products name or service you provide and sell/quote" />
            </div>

            {/* Product Type */}
            <div className="product-form__field">
              <label className="product-form__label" htmlFor="product_type">
                Product Type <span className="product-form__required">*</span>
              </label>
              <select
                id="product_type"
                className={`product-form__select ${errors.product_type ? 'product-form__input--error' : ''}`}
                value={formData.product_type}
                onChange={e => handleChange('product_type', e.target.value)}
                disabled={typeIsLocked || typeIsDisabledByLinks}
              >
                {PRODUCT_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {typeIsDisabledByLinks && (
                <span className="product-form__field-help">
                  Product type cannot be changed when the product has linked requests.
                </span>
              )}
              {errors.product_type && (
                <span className="product-form__field-error">{errors.product_type}</span>
              )}
            </div>

            {/* Release Type */}
            <div className="product-form__field">
              <label className="product-form__label" htmlFor="release_type">
                Release Type <span className="product-form__required">*</span>
              </label>
              <select
                id="release_type"
                className={`product-form__select ${errors.release_type ? 'product-form__input--error' : ''}`}
                value={formData.release_type}
                onChange={e => handleChange('release_type', e.target.value)}
              >
                {RELEASE_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {errors.release_type && (
                <span className="product-form__field-error">{errors.release_type}</span>
              )}
            </div>

            {/* Product Code */}
            <div className="product-form__field">
              <label className="product-form__label" htmlFor="product_code">
                Product Code <span className="product-form__required">*</span>
              </label>
              <input
                id="product_code"
                type="text"
                className={`product-form__input ${errors.product_code ? 'product-form__input--error' : ''}`}
                value={formData.product_code}
                onChange={e => handleChange('product_code', e.target.value)}
                placeholder="Enter product code"
              />
              {errors.product_code && <span className="product-form__field-error">{errors.product_code}</span>}
              <FieldHelp text="Is an internal identifier of the product record. Often used for reporting or to support custom functions. Leveraging Code v. Name in functions is a best practice." />
            </div>

            {/* Description */}
            <div className="product-form__field product-form__field--full">
              <label className="product-form__label" htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                className="product-form__textarea"
                value={formData.description}
                onChange={e => handleChange('description', e.target.value)}
                placeholder="Enter description (optional)"
                rows={4}
              />
              <FieldHelp text="Is a logical description of the product from your businesses perspective" />
            </div>

            {/* Categorization */}
            <div className="product-form__field product-form__field--full">
              <label className="product-form__label" htmlFor="categorization">
                Categorization <span className="product-form__required">*</span>
              </label>
              <select
                id="categorization"
                className={`product-form__select ${errors.categorization ? 'product-form__input--error' : ''}`}
                value={formData.categorization}
                onChange={e => handleChange('categorization', e.target.value)}
              >
                <option value="">Select categorization</option>
                <option value="Cloud-Native Application">Cloud-Native Application</option>
                <option value="Cloud-Native Content">Cloud-Native Content</option>
                <option value="Cloud-Native Framework">Cloud-Native Framework</option>
                <option value="Cloud-Native Service">Cloud-Native Service</option>
                <option value="Hybrid Application">Hybrid Application</option>
                <option value="Hybrid Content">Hybrid Content</option>
                <option value="Hybrid Framework">Hybrid Framework</option>
                <option value="Hybrid Service">Hybrid Service</option>
                <option value="Other">Other</option>
              </select>
              {errors.categorization && (
                <span className="product-form__field-error">{errors.categorization}</span>
              )}
            </div>

            {/* Owner */}
            <div className="product-form__field">
              <label className="product-form__label" htmlFor="owner">
                Owner <span className="product-form__required">*</span>
              </label>
              <select
                id="owner"
                className={`product-form__select ${errors.owner ? 'product-form__input--error' : ''}`}
                value={formData.owner}
                onChange={e => handleChange('owner', e.target.value)}
              >
                <option value="">Select owner</option>
                {users.map(user => (
                  <option key={user.id} value={String(user.id)}>
                    {user.firstName || user.first_name} {user.lastName || user.last_name}
                    {user.email ? ` (${user.email})` : ''}
                  </option>
                ))}
              </select>
              {errors.owner && (
                <span className="product-form__field-error">{errors.owner}</span>
              )}
            </div>

            {/* Status */}
            <div className="product-form__field">
              <label className="product-form__label" htmlFor="status">
                Status <span className="product-form__required">*</span>
              </label>
              <select
                id="status"
                className={`product-form__select ${errors.status ? 'product-form__input--error' : ''}`}
                value={formData.status}
                onChange={e => handleChange('status', e.target.value)}
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {errors.status && (
                <span className="product-form__field-error">{errors.status}</span>
              )}
            </div>

          </div>
        </div>
        )} {/* end currentSection === 0 */}

        {/* ── Section 1: Detail (Commercial + Technical) ───────────────────── */}
        {currentSection === 1 && (formData.product_type === 'COMMERCIAL' || formData.product_type === 'TECHNICAL') && (
          <div className="product-form__section">
            <h2 className="product-form__section-title">Detail</h2>
            <div className="product-form__grid">

              {/* Commercial-only fields */}
              {formData.product_type === 'COMMERCIAL' && (
                <>
                  {/* Business Unit */}
                  <div className="product-form__field">
                    <label className="product-form__label" htmlFor="business_unit">
                      Business Unit <span className="product-form__required">*</span>
                    </label>
                    <select
                      id="business_unit"
                      className={`product-form__select ${errors.business_unit ? 'product-form__input--error' : ''}`}
                      value={formData.business_unit}
                      onChange={e => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, business_unit: val, performance_matrix_group: '', product_family: '' }));
                        setErrors(prev => ({ ...prev, business_unit: '', performance_matrix_group: '', product_family: '' }));
                      }}
                    >
                      <option value="">Select business unit</option>
                      {BUSINESS_UNIT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {errors.business_unit && <span className="product-form__field-error">{errors.business_unit}</span>}
                  </div>

                  {/* Performance Matrix Group */}
                  <div className="product-form__field">
                    <label className="product-form__label" htmlFor="performance_matrix_group">
                      Performance Matrix Group <span className="product-form__required">*</span>
                    </label>
                    <select
                      id="performance_matrix_group"
                      className={`product-form__select ${errors.performance_matrix_group ? 'product-form__input--error' : ''}`}
                      value={formData.performance_matrix_group}
                      onChange={e => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, performance_matrix_group: val, product_family: '' }));
                        setErrors(prev => ({ ...prev, performance_matrix_group: '', product_family: '' }));
                      }}
                    >
                      <option value="">Select performance matrix group</option>
                      {availablePMGs.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {errors.performance_matrix_group && <span className="product-form__field-error">{errors.performance_matrix_group}</span>}
                  </div>

                  {/* Product Family */}
                  <div className="product-form__field">
                    <label className="product-form__label" htmlFor="product_family">
                      Product Family <span className="product-form__required">*</span>
                    </label>
                    <select
                      id="product_family"
                      className={`product-form__select ${errors.product_family ? 'product-form__input--error' : ''}`}
                      value={formData.product_family}
                      onChange={e => handleChange('product_family', e.target.value)}
                    >
                      <option value="">Select product family</option>
                      {availablePFs.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {errors.product_family && <span className="product-form__field-error">{errors.product_family}</span>}
                  </div>

                  {/* Based On */}
                  <div className="product-form__field product-form__field--full">
                    <label className="product-form__label" htmlFor="based_on">
                      Based On <span className="product-form__required">*</span>
                    </label>
                    <input
                      id="based_on"
                      type="text"
                      className={`product-form__input ${errors.based_on ? 'product-form__input--error' : ''}`}
                      value={formData.based_on}
                      onChange={e => handleChange('based_on', e.target.value)}
                    />
                    {errors.based_on && <span className="product-form__field-error">{errors.based_on}</span>}
                  </div>

                  {/* Availability Date */}
                  <div className="product-form__field">
                    <label className="product-form__label" htmlFor="availability_date">Availability Date</label>
                    <input
                      id="availability_date"
                      type="date"
                      className="product-form__input"
                      value={formData.availability_date}
                      onChange={e => handleChange('availability_date', e.target.value)}
                    />
                    <FieldHelp text="When product is available for Sale" />
                  </div>

                  {/* Installation */}
                  <div className="product-form__field">
                    <label className="product-form__label" htmlFor="installation">Installation</label>
                    <select
                      id="installation"
                      className="product-form__select"
                      value={formData.installation}
                      onChange={e => handleChange('installation', e.target.value)}
                    >
                      <option value="">Select installation type</option>
                      {INSTALLATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>

                  {/* End Of Life Date */}
                  <div className="product-form__field">
                    <label className="product-form__label" htmlFor="end_of_life_date">End Of Life Date</label>
                    <input
                      id="end_of_life_date"
                      type="date"
                      className="product-form__input"
                      value={formData.end_of_life_date}
                      onChange={e => handleChange('end_of_life_date', e.target.value)}
                    />
                    <FieldHelp text="Date at which product can no longer be sold" />
                  </div>

                  {/* Discontinued Date */}
                  <div className="product-form__field">
                    <label className="product-form__label" htmlFor="discontinued_date">Discontinued Date</label>
                    <input
                      id="discontinued_date"
                      type="date"
                      className="product-form__input"
                      value={formData.discontinued_date}
                      onChange={e => handleChange('discontinued_date', e.target.value)}
                    />
                    <FieldHelp text="Date when product will no longer be supported in functions including ordering" />
                  </div>

                  {/* Core Product Type */}
                  <div className="product-form__field">
                    <label className="product-form__label" htmlFor="core_product_type">Core Product Type</label>
                    <select
                      id="core_product_type"
                      className="product-form__select"
                      value={formData.core_product_type}
                      onChange={e => handleChange('core_product_type', e.target.value)}
                    >
                      <option value="">Select core product type</option>
                      {CORE_PRODUCT_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <FieldHelp text="This should only be populated for 'core' products. It's expected to be empty for most products. We need it populated for the core PC, BC, and CC product." />
                  </div>

                </>
              )}

              {/* Technical-only fields */}
              {formData.product_type === 'TECHNICAL' && (
                <>
                <div className="product-form__field product-form__field--full">
                  <label className="product-form__label" htmlFor="fulfilment_qty_calc_method">
                    Fulfilment Quantity Calculation Method
                  </label>
                  <select
                    id="fulfilment_qty_calc_method"
                    className="product-form__select"
                    value={formData.fulfilment_qty_calc_method}
                    onChange={e => handleChange('fulfilment_qty_calc_method', e.target.value)}
                  >
                    <option value="">Select method</option>
                    {FULFILMENT_QTY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <FieldHelp text="Always One - Used when the quantity of the commercial product SHOULDN'T be inherited for the technical product. Eg PolicyCenter. Aggregate - Used when the quantity of the contributing commercial products SHOULD be inherited for the technical product. Eg Guidewire Credits." />
                </div>

                {/* Polaris ID */}
                <div className="product-form__field">
                  <label className="product-form__label" htmlFor="polaris_id">Polaris ID</label>
                  <input
                    id="polaris_id"
                    type="text"
                    className="product-form__input"
                    value={formData.polaris_id}
                    onChange={e => handleChange('polaris_id', e.target.value)}
                    placeholder="Enter Polaris ID"
                  />
                </div>

                {/* Has Customer Update Adoption Criteria */}
                <div className="product-form__field">
                  <label className="product-form__label" htmlFor="has_customer_update_adoption_criteria">
                    Has Customer Update Adoption Criteria? <span className="product-form__required">*</span>
                  </label>
                  <select
                    id="has_customer_update_adoption_criteria"
                    className={`product-form__select${errors.has_customer_update_adoption_criteria ? ' product-form__select--error' : ''}${formData.release_type !== 'GA' ? ' product-form__select--disabled' : ''}`}
                    value={formData.has_customer_update_adoption_criteria}
                    onChange={e => handleChange('has_customer_update_adoption_criteria', e.target.value)}
                    disabled={formData.release_type !== 'GA'}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                  {formData.release_type !== 'GA' ? (
                    <span className="product-form__field-help">Only available for GA release type.</span>
                  ) : (
                    <span className="product-form__field-help">Select Yes only if a key technical product or capability which requires update planning with customers.</span>
                  )}
                  {errors.has_customer_update_adoption_criteria && (
                    <p className="product-form__error">{errors.has_customer_update_adoption_criteria}</p>
                  )}
                </div>
                </>
              )}

              {/* Commercial + Technical fields */}
              <div className="product-form__field product-form__field--full">
                <label className="product-form__label" htmlFor="predecessor_product">Predecessor Product</label>
                <input
                  id="predecessor_product"
                  type="text"
                  className="product-form__input"
                  value={formData.predecessor_product}
                  onChange={e => handleChange('predecessor_product', e.target.value)}
                  placeholder="Enter predecessor product name (optional)"
                />
              </div>

              {/* PPP Apply — Technical only */}
              {formData.product_type === 'TECHNICAL' && (
                <div className="product-form__field product-form__field--full">
                  <label className="product-form__label">PPP Apply?</label>
                  <div className="product-form__radio-group">
                    {['Yes', 'No'].map(opt => (
                      <label key={opt} className="product-form__radio-label">
                        <input
                          type="radio"
                          name="ppp_apply"
                          value={opt === 'Yes' ? 'true' : 'false'}
                          checked={formData.ppp_apply === (opt === 'Yes' ? 'true' : 'false')}
                          onChange={e => handleChange('ppp_apply', e.target.value)}
                          className="product-form__radio"
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                  {pppCheckMessage && (
                    <p className="product-form__field-help">{pppCheckMessage}</p>
                  )}
                </div>
              )}

            </div>
          </div>
        )}

        {/* ── Section 2: Provisioning (Technical only) ─────────────────────── */}
        {currentSection === 2 && formData.product_type === 'TECHNICAL' && (
          <div className="product-form__section">
            <h2 className="product-form__section-title">Provisioning</h2>
            <div className="product-form__grid">

              {/* Provisioning Required */}
              <div className="product-form__field product-form__field--full">
                <label className="product-form__label">
                  Provisioning Required? <span className="product-form__required">*</span>
                </label>
                <div className="product-form__radio-group">
                  {['Yes', 'No'].map(opt => (
                    <label key={opt} className="product-form__radio-label">
                      <input
                        type="radio"
                        name="provisioning_required"
                        value={opt === 'Yes' ? 'true' : 'false'}
                        checked={formData.provisioning_required === (opt === 'Yes' ? 'true' : 'false')}
                        onChange={e => handleChange('provisioning_required', e.target.value)}
                        className="product-form__radio"
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
                {errors.provisioning_required && <span className="product-form__field-error">{errors.provisioning_required}</span>}
              </div>

              {formData.provisioning_required === 'true' && (
                <>
                  {/* Provisioning Team */}
                  <div className="product-form__field product-form__field--full">
                    <label className="product-form__label" htmlFor="provisioning_team">
                      Provisioning Team <span className="product-form__required">*</span>
                    </label>
                    <select
                      id="provisioning_team"
                      className={`product-form__select ${errors.provisioning_team ? 'product-form__input--error' : ''}`}
                      value={formData.provisioning_team}
                      onChange={e => handleChange('provisioning_team', e.target.value)}
                    >
                      <option value="">Select provisioning team</option>
                      {PROVISIONING_TEAM_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {errors.provisioning_team && <span className="product-form__field-error">{errors.provisioning_team}</span>}
                  </div>

                  {/* Provisioning Automated */}
                  <div className="product-form__field">
                    <label className="product-form__label">
                      Provisioning Automated? <span className="product-form__required">*</span>
                    </label>
                    <div className="product-form__radio-group">
                      {['Yes', 'No'].map(opt => (
                        <label key={opt} className="product-form__radio-label">
                          <input
                            type="radio"
                            name="provisioning_automated"
                            value={opt === 'Yes' ? 'true' : 'false'}
                            checked={formData.provisioning_automated === (opt === 'Yes' ? 'true' : 'false')}
                            onChange={e => handleChange('provisioning_automated', e.target.value)}
                            className="product-form__radio"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                    {errors.provisioning_automated && <span className="product-form__field-error">{errors.provisioning_automated}</span>}
                  </div>

                  {/* Provisioned in NPE/Prod */}
                  <div className="product-form__field">
                    <label className="product-form__label" htmlFor="provisioned_in_npe_prod">
                      Provisioned in NPE/Prod <span className="product-form__required">*</span>
                    </label>
                    <select
                      id="provisioned_in_npe_prod"
                      className={`product-form__select ${errors.provisioned_in_npe_prod ? 'product-form__input--error' : ''}`}
                      value={formData.provisioned_in_npe_prod}
                      onChange={e => handleChange('provisioned_in_npe_prod', e.target.value)}
                    >
                      <option value="">Select environment</option>
                      {PROVISIONED_IN_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {errors.provisioned_in_npe_prod && <span className="product-form__field-error">{errors.provisioned_in_npe_prod}</span>}
                  </div>

                  {/* Has Provisioning Action on Renewal */}
                  <div className="product-form__field product-form__field--full">
                    <label className="product-form__label">
                      Has Provisioning Action on Renewal <span className="product-form__required">*</span>
                    </label>
                    <div className="product-form__radio-group">
                      {['Yes', 'No'].map(opt => (
                        <label key={opt} className="product-form__radio-label">
                          <input
                            type="radio"
                            name="has_provisioning_action_on_renewal"
                            value={opt === 'Yes' ? 'true' : 'false'}
                            checked={formData.has_provisioning_action_on_renewal === (opt === 'Yes' ? 'true' : 'false')}
                            onChange={e => handleChange('has_provisioning_action_on_renewal', e.target.value)}
                            className="product-form__radio"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                    {errors.has_provisioning_action_on_renewal && <span className="product-form__field-error">{errors.has_provisioning_action_on_renewal}</span>}
                    <FieldHelp text="Controls whether or not a product requires additional provisioning activities to take place at time of renewal. Example is Guidewire Credit" />
                  </div>
                </>
              )}

            </div>
          </div>
        )}
      </div>

      <div className="product-form__footer">
        <button
          className="product-form__btn product-form__btn--secondary"
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <div className="product-form__footer-nav">
          {currentSection > 0 && (
            <button
              className="product-form__btn product-form__btn--secondary"
              onClick={handleSectionBack}
              disabled={saving}
            >
              Back
            </button>
          )}
          {currentSection < steps.length - 1 ? (
            <button
              className="product-form__btn product-form__btn--primary"
              onClick={handleNext}
              disabled={saving}
            >
              Next
            </button>
          ) : (
            <button
              className="product-form__btn product-form__btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="product-form__loading">
        <div className="product-form__spinner" />
        <p>Loading product...</p>
      </div>
    );
  }

  // ── Modal mode ──────────────────────────────────────────────────────────────
  if (isModal) {
    return (
      <div className="product-form-modal__backdrop" onClick={e => { if (e.target === e.currentTarget) handleCancel(); }}>
        <div className="product-form-modal__container">
          <div className="product-form-modal__header">
            <h2 className="product-form-modal__title">{pageTitle}</h2>
            <button className="product-form-modal__close" onClick={handleCancel} title="Close">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          {formContent}
        </div>
      </div>
    );
  }

  // ── Full-page mode ──────────────────────────────────────────────────────────
  return (
    <div className="product-form">
      <div className="product-form__header">
        <h1 className="product-form__title">{pageTitle}</h1>
      </div>
      {formContent}
    </div>
  );
};

export default ProductForm;
